import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import { useEventCallback } from '../../common/utils/hooks';
import { useTheme } from '../theme';
import Sheet from '../design/components/Sheet';
import { SHEET_CLOSE_MS } from '../design/components/SheetModal.web';

// Web stand-in for react-native-modal-datetime-picker (date mode): the browser's
// date field in a design Sheet. Picking a date confirms it; the close button,
// backdrop and back gesture cancel. onHide runs once the sheet has slid away, like the
// native picker's onHide.
const DateTimePickerModal = ({
	isVisible,
	onConfirm,
	onCancel,
	onHide,
	date,
	minimumDate,
}) => {
	const { theme: { ui } } = useTheme();
	const wasVisibleRef = useRef(isVisible);
	const handleHide = useEventCallback(() => onHide?.());

	useEffect(() => {
		const wasVisible = wasVisibleRef.current;
		wasVisibleRef.current = isVisible;
		if (!wasVisible || isVisible) {
			return undefined;
		}
		const timer = setTimeout(handleHide, SHEET_CLOSE_MS);
		return () => clearTimeout(timer);
	}, [isVisible, handleHide]);

	const { colors, radius, space, touch, type } = ui;

	const handleDateChange = useEventCallback(event => {
		const newDate = new Date(event.target.value);
		onConfirm(newDate);
	});

	const formatDateForInput = useCallback(date => moment(date).format('YYYY-MM-DD'), []);

	const minDate = useMemo(() => (minimumDate ? formatDateForInput(minimumDate) : undefined), [minimumDate, formatDateForInput]);

	// A DOM input takes CSS, so the design tokens are applied directly.
	const inputStyle = {
		boxSizing: 'border-box',
		width: '100%',
		height: touch.min,
		padding: `0 ${space.md}px`,
		fontSize: type.body.fontSize,
		fontFamily: type.body.fontFamily,
		color: colors.text,
		backgroundColor: colors.card,
		border: `1px solid ${colors.borderStrong}`,
		borderRadius: radius.card,
		colorScheme: ui.isDark ? 'dark' : 'light',
		marginBottom: space.sm,
	};

	return (
		<Sheet isVisible={isVisible} onClose={onCancel} title="Select date">
			<input
				type="date"
				aria-label="Date"
				value={formatDateForInput(date)}
				onChange={handleDateChange}
				min={minDate}
				style={inputStyle}
			/>
		</Sheet>
	);
};

export default DateTimePickerModal;
